import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select, Button, Card, Row, Col, DatePicker, Typography, Divider, Empty, message } from 'antd';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { getAllCreditNotes, getNextCreditNoteNumber, createStandaloneCreditNote } from '../utils/api';
import { format } from 'date-fns';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import { formatIndianCurrency } from '../utils/numberToWords';
import dayjs from 'dayjs';
import { UOM_OPTIONS } from '../utils/uom';
import CodeTypeSelect, { codeTypeFromIsServc, isServcFromCodeType } from '../components/CodeTypeSelect';
import { isViewer } from '../utils/auth';

const { Title, Text } = Typography;

function getFinancialYear(date) {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = d.getMonth();
    return month < 3 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  } catch {
    return null;
  }
}

export default function CreditNotes({ invoices = [], clients = [], config = {}, onInvoiceCreated }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState('');
  const [items, setItems] = useState([{ description: '', rate: 0, qty: 1, amount: 0, tax: 0, taxAmount: 0, hsn: '', IsServc: 'Y', uom: '' }]);
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [financialYear, setFinancialYear] = useState(() => getFinancialYear(new Date()));
  const [placeOfSupply, setPlaceOfSupply] = useState('Karnataka');
  const [creditNoteNumber, setCreditNoteNumber] = useState('');
  const [creditReason, setCreditReason] = useState('other');
  const [creditDescription, setCreditDescription] = useState('');
  const [referenceInvoiceId, setReferenceInvoiceId] = useState('');
  const [referenceInvoiceNumber, setReferenceInvoiceNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      try {
        const list = await getAllCreditNotes();
        if (mounted) setCreditNotes(Array.isArray(list) ? list : []);
      } catch {
        if (mounted) setCreditNotes([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    getNextCreditNoteNumber().then((d) => setCreditNoteNumber(d?.creditNoteNumber || '')).catch(() => { });
  }, []);

  const clientInvoices = useMemo(() => {
    if (!clientId) return [];
    return invoices.filter((inv) => {
      const invClientId = inv.client?._id || inv.client?.id || inv.client;
      return String(invClientId) === String(clientId) && !inv.isCancelled && (inv.invoiceType || 'rent') !== 'credit_note';
    });
  }, [invoices, clientId]);

  const selectedClientData = useMemo(() => {
    if (!clientId) return null;
    return clients.find(c => (c._id || c.id) === clientId);
  }, [clients, clientId]);

  const formatInvoiceLabel = (inv) => {
    try {
      const num = typeof inv.invoiceNumber === 'string'
        ? inv.invoiceNumber
        : formatInvoiceNumber(inv.invoiceNumber, inv.financialYear, config);
      const dateStr = inv.invoiceDate ? format(new Date(inv.invoiceDate), 'dd/MM/yyyy') : '';
      const total = inv.totals?.total != null ? inv.totals.total : '';
      return `${num}${dateStr ? ' | ' + dateStr : ''}${total ? ' | ₹' + total.toLocaleString() : ''}`;
    } catch {
      return String(inv.invoiceNumber || '').trim() || 'Invoice';
    }
  };

  const setItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const row = { ...next[idx], [field]: value };
      if (field === 'rate' || field === 'qty' || field === 'tax') {
        const rate = Number(row.rate || 0);
        const qty = Number(row.qty || 1);
        row.amount = rate * qty;
        const taxPct = Number(row.tax || 0);
        row.taxAmount = (row.amount * taxPct) / 100;
      }
      next[idx] = row;
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, { description: '', rate: 0, qty: 1, amount: 0, tax: 0, taxAmount: 0, hsn: '', IsServc: 'Y', uom: '' }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, x) => s + Number(x.amount || 0), 0);
    const totalTax = items.reduce((s, x) => s + Number(x.taxAmount || 0), 0);
    return { subtotal, totalTax, total: subtotal + totalTax };
  }, [items]);

  const createNote = async () => {
    if (!clientId) { message.warning('Please select a client'); return; }
    if (!creditNoteNumber || !invoiceDate || !financialYear || !placeOfSupply) { message.warning('Please fill required fields'); return; }
    if (totals.total <= 0) { message.warning('Total must be greater than zero'); return; }
    const missingUomIdx = items.findIndex(it => (it.IsServc || 'N') !== 'Y' && !it.uom);
    if (missingUomIdx !== -1) { message.warning(`Row ${missingUomIdx + 1}: UOM is required for items with HSN Code.`); return; }
    setSaving(true);
    try {
      const payload = {
        clientId,
        originalInvoiceId: referenceInvoiceId || undefined,
        items: items.map((it) => ({ description: it.description, rate: it.rate, qty: it.qty, amount: it.amount, hsn: it.hsn, uom: it.uom || '', IsServc: it.IsServc, tax: it.tax || 0, taxAmount: it.taxAmount || 0, totalAmount: (Number(it.amount || 0) + Number(it.taxAmount || 0)) })),
        creditReason,
        creditDescription,
        creditNoteNumber,
        invoiceDate,
        financialYear,
        placeOfSupply,
        referenceInvoiceNumber: referenceInvoiceNumber || undefined
      };
      const note = await createStandaloneCreditNote(payload);
      setCreditNotes((prev) => [note, ...prev]);
      if (onInvoiceCreated) {
        onInvoiceCreated(note);
      }
      setItems([{ description: '', rate: 0, qty: 1, amount: 0, hsn: '', IsServc: 'Y', uom: '' }]);
      setReferenceInvoiceId('');
      getNextCreditNoteNumber().then((d) => setCreditNoteNumber(d?.creditNoteNumber || ''));
      message.success('Credit note created successfully!');
    } catch (e) {
      message.error(e.message || 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  if (isViewer()) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Empty description={<Text type="secondary">Your role is read-only. Creating credit notes is not allowed.</Text>} />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header Section */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Create Credit Note</Title>
          <Text type="secondary">Create and manage credit notes for refunds and adjustments</Text>
        </Col>
      </Row>

      {/* General Information Card */}
      <Card
        title={<><FileText size={18} style={{ marginRight: 8, color: '#1890ff' }} />General Information</>}
        style={{ marginBottom: 24, borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
        styles={{ body: { padding: 24 } }}
      >
        <Row gutter={24}>
          {/* Column 1 */}
          <Col span={8}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Client Selection</Text></div>
              <Select
                showSearch
                style={{ width: '100%' }}
                value={clientId || undefined}
                placeholder="Select client"
                optionFilterProp="label"
                onChange={(v) => { setClientId(v); setReferenceInvoiceId(''); setReferenceInvoiceNumber(''); }}
                options={clients.map((c) => ({ value: c._id || c.id, label: c.registeredName || c.name }))}
                allowClear
              />
            </div>
            {selectedClientData && (
              <div style={{ marginBottom: 12, marginTop: '-22px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>{selectedClientData.registeredName || selectedClientData.name}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>GSTIN: {selectedClientData.gstin || '-'}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedClientData.billToAddress || 'No address available'}</div>
              </div>
            )}
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Invoice Reference</Text></div>
              <Select
                showSearch
                style={{ width: '100%' }}
                value={referenceInvoiceId || undefined}
                placeholder={clientId ? 'Select invoice (optional)' : 'Select client first'}
                optionFilterProp="label"
                onChange={(v) => {
                  setReferenceInvoiceId(v);
                  const selectedInv = clientInvoices.find(inv => inv._id === v);
                  if (selectedInv) {
                    const formattedNum = typeof selectedInv.invoiceNumber === 'string' && selectedInv.invoiceNumber.includes('/')
                      ? selectedInv.invoiceNumber
                      : formatInvoiceNumber(selectedInv.invoiceNumber, selectedInv.financialYear, config);
                    setReferenceInvoiceNumber(formattedNum);
                  }
                }}
                disabled={!clientId}
                options={clientInvoices.map((inv) => ({
                  value: inv._id,
                  label: formatInvoiceLabel(inv)
                }))}
                allowClear
              />
            </div>
          </Col>

          {/* Column 2 */}
          <Col span={8}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Credit Note Date</Text></div>
              <DatePicker
                style={{ width: '100%' }}
                value={invoiceDate ? dayjs(invoiceDate) : null}
                onChange={(d) => setInvoiceDate(d ? d.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                placeholder="Select Date"
                allowClear
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Financial Year</Text></div>
              <Input
                value={financialYear || ''}
                onChange={(e) => setFinancialYear(e.target.value)}
                placeholder="2024-2025"
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Place of Supply</Text></div>
              <Input
                value={placeOfSupply}
                onChange={(e) => setPlaceOfSupply(e.target.value)}
                placeholder="Karnataka"
              />
            </div>
          </Col>

          {/* Column 3 */}
          <Col span={8}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Credit Note No.</Text></div>
              <Input
                value={creditNoteNumber}
                disabled
                style={{ color: '#000', cursor: 'default' }}
                className="disabled-input-text"
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Credit Reason</Text></div>
              <Select
                style={{ width: '100%' }}
                value={creditReason}
                onChange={setCreditReason}
                options={[
                  { value: 'rate_difference', label: 'Rate Difference' },
                  { value: 'sales_return', label: 'Sales Return' },
                  { value: 'post_sale_discount', label: 'Post Sale Discount' },
                  { value: 'service_deficiency', label: 'Service Deficiency' },
                  { value: 'other', label: 'Other' }
                ]}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Description</Text></div>
              <Input
                value={creditDescription}
                onChange={(e) => setCreditDescription(e.target.value)}
                placeholder="Additional details (optional)"
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Line Items Card */}
      <Card
        title={<><FileText size={18} style={{ marginRight: 8, color: '#1890ff' }} />Line Items</>}
        style={{ marginBottom: 24, borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead style={{ background: '#fafafa' }}>
              <tr>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 50, textAlign: 'center', color: '#64748b' }}>Sr.</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'left', color: '#64748b' }}>HSN/SAC</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'left', color: '#64748b' }}>Description</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>Rate</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 80, textAlign: 'center', color: '#64748b' }}>Qty</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 110, textAlign: 'left', color: '#64748b' }}>UOM</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>Amount</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 80, textAlign: 'center', color: '#64748b' }}>GST %</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>GST Amt</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>Total</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 110, textAlign: 'center', color: '#64748b' }}>Code Type</th>
                <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => {
                const rowTotal = Number(row.amount || 0) + Number(row.taxAmount || 0);
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{idx + 1}</td>
                    <td style={{ padding: '8px' }}>
                      <Input
                        bordered={false}
                        placeholder="HSN"
                        value={row.hsn}
                        onChange={(e) => setItem(idx, 'hsn', e.target.value)}
                        style={{ padding: '4px 8px' }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <Input
                        bordered={false}
                        placeholder="Item Description"
                        value={row.description}
                        onChange={(e) => setItem(idx, 'description', e.target.value)}
                        style={{ fontWeight: 500 }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <Input
                        bordered={false}
                        placeholder="0.00"
                        value={row.rate || ''}
                        onChange={(e) => setItem(idx, 'rate', e.target.value)}
                        style={{ textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <Input
                        bordered={false}
                        placeholder="1"
                        value={row.qty || ''}
                        onChange={(e) => setItem(idx, 'qty', e.target.value)}
                        style={{ textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <Select
                        variant="borderless"
                        style={{ width: '100%' }}
                        value={row.uom || undefined}
                        onChange={(v) => setItem(idx, 'uom', v)}
                        placeholder="UOM"
                        options={UOM_OPTIONS}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {formatIndianCurrency(Number(row.amount || 0))}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <Select
                        variant="borderless"
                        style={{ width: '100%' }}
                        value={row.tax ? String(row.tax) : '0'}
                        onChange={(v) => setItem(idx, 'tax', Number(v))}
                        options={[
                          { label: '0%', value: '0' },
                          { label: '5%', value: '5' },
                          { label: '12%', value: '12' },
                          { label: '18%', value: '18' },
                          { label: '28%', value: '28' }
                        ]}
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {formatIndianCurrency(Number(row.taxAmount || 0))}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                      {formatIndianCurrency(rowTotal)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <CodeTypeSelect
                        size="small"
                        value={codeTypeFromIsServc(row.IsServc)}
                        onChange={(v) => setItem(idx, 'IsServc', isServcFromCodeType(v))}
                      />
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {items.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<Trash2 size={16} />}
                          onClick={() => removeItem(idx)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add Item & Totals Section */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="dashed"
            icon={<Plus size={16} />}
            onClick={addItem}
          >
            Add Item
          </Button>

          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary">Subtotal</Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{formatIndianCurrency(totals.subtotal)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary">GST</Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{formatIndianCurrency(totals.totalTax)}</div>
            </div>
            <div style={{ textAlign: 'right', background: '#f0f9ff', padding: '8px 16px', borderRadius: 8 }}>
              <Text type="secondary">Total</Text>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff' }}>{formatIndianCurrency(totals.total)}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={createNote}
          disabled={saving || !clientId}
          loading={saving}
          style={{ minWidth: 180 }}
        >
          {saving ? 'Creating...' : 'Create Credit Note'}
        </Button>
      </div>
    </div>
  );
}
